import React, { useState, useEffect, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, CircleAlert, Eye, Image as ImageIcon } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import CourseCoverPicker, { buildCoverDataUri, DEFAULT_COVER_DESIGN } from '../../components/CourseCoverPicker/CourseCoverPicker';
import FormErrorBoundary from '../../components/FormErrorBoundary/FormErrorBoundary';
import FormErrorDisplay from '../../components/FormErrorDisplay/FormErrorDisplay';
import CourseListCard from '../Courses/CourseListCard';
import { createCourse } from '../../api/courses';
import { submitFormWithRetry } from '../../utils/formErrorHandler';
import { getNetworkMonitor } from '../../utils/networkMonitor';
import { currencyLabel, formatNumber } from '../../utils/datetime';
import Dropdown from '../../components/Dropdown/Dropdown';
import './CourseCreation.css';

const TITLE_MIN = 3;
const TITLE_MAX = 100;
// A price is whole so'm. Ten digits (9 999 999 999) is far past any course and keeps the number exact.
const PRICE_MAX_DIGITS = 10;

// The cover the picker shows before it is touched (brand yellow, the language globe: Darsly teaches
// languages). It is kept in the form from the start, so what the picker and the card preview show is
// what the course is created with; its title then follows the course title as it is typed.
const DEFAULT_COVER = DEFAULT_COVER_DESIGN;

// A create that timed out, got a 5xx or lost its connection is never re-sent automatically (the
// course may already exist), so the error shown says to check the list before trying again by hand.
function mayHaveBeenCreated(err) {
  if (!navigator.onLine) return false; // offline: the request never left
  // No HTTP status at all (our own timer, the client's timeout, a dropped connection) or a 5xx: the
  // server may have inserted the row before the answer was lost. (The client's network and timeout
  // messages are translated, so the text cannot be matched; the missing status is the signal.)
  return typeof err?.code !== 'number' || err.code >= 500;
}

// "1 500 000" with the caret kept where the reader left it: the index just after the n-th digit.
function positionAfterDigits(text, digits) {
  if (digits <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (/\d/.test(text[i])) {
      seen += 1;
      if (seen === digits) return i + 1;
    }
  }
  return text.length;
}

/**
 * One slot under a field for its hint and its error. The error replaces the hint in the SAME cell
 * (both stay laid out, one is just hidden), so showing it never moves anything below it: a click that
 * lands next to a field that has just lost focus is not shifted onto the wrong element.
 */
function FieldNote({ id, hint, error, className = '' }) {
  return (
    <div className={`ccx-note${error ? ' has-error' : ''}${className ? ` ${className}` : ''}`} aria-live="polite">
      {hint && <p id={`${id}-hint`} className="ccx-hint">{hint}</p>}
      {error && (
        <p id={`${id}-error`} className="ccx-error">
          <CircleAlert aria-hidden="true" />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}

// A card's head: a 40px tone chip, a title and one line saying what the card is for.
function CardHead({ id, icon, tone, title, desc }) {
  return (
    <div className="section-head ccx-head">
      <span className="ui-icon-chip ui-icon-chip--md" data-tone={tone} aria-hidden="true">{icon}</span>
      <div className="ccx-head-text">
        <h2 id={id} className="section-head__title">{title}</h2>
        {desc && <p className="section-head__desc">{desc}</p>}
      </div>
    </div>
  );
}

/**
 * Course creation form — mounted only at /courses/create. Editing an existing
 * course happens in the CourseOverview workspace (/courses/edit/:id), which
 * owns its own Settings tab; this component has no edit mode.
 */
const CourseCreation = () => {
  const navigate = useNavigate();
  const { t, lang } = useI18n();

  const [createForm, setCreateForm] = useState(() => ({
    title: '',
    description: '',
    level: 'BEGINNER',
    // Digits only ("1500000"); the field shows them in groups.
    price: '',
    thumbnail: buildCoverDataUri(DEFAULT_COVER),
  }));
  const [creating, setCreating] = useState(false);
  // The failure's own translated sentence (the banner never prints the server's text).
  const [formErrorMessage, setFormErrorMessage] = useState(null);

  // A field's error shows once it has been left (or a create was attempted), never while the
  // reader is still typing their first words.
  const [touched, setTouched] = useState({});
  const [formRetryCount, setFormRetryCount] = useState(0);
  const [formRetryError, setFormRetryError] = useState(null);
  const [autoRetryDelay, setAutoRetryDelay] = useState(null);
  const networkMonitorRef = useRef(null);

  const titleRef = useRef(null);
  const priceRef = useRef(null);
  // Where the caret goes after the price is re-grouped (digits to its left), or null.
  const priceCaretRef = useRef(null);

  // Debounce timer for double-click prevention
  const submitDebounceRef = useRef(null);
  useEffect(() => () => clearTimeout(submitDebounceRef.current), []);

  // Setup network monitoring and auto-retry on reconnection
  useEffect(() => {
    networkMonitorRef.current = getNetworkMonitor();

    const unsubscribe = networkMonitorRef.current.onReconnect(() => {
      if (formRetryError && formRetryCount < 3) {
        // Auto-retry on network restoration
        setFormRetryError(null);
        setFormRetryCount(0);
      }
    });

    return () => unsubscribe();
  }, [formRetryError, formRetryCount]);

  // What is wrong with the form right now. Worked out from the values (not stored), so it is
  // always current and follows a language switch.
  const errors = useMemo(() => {
    const out = {};
    const title = createForm.title.trim();
    const label = t('courseCreate.field.title');
    if (!title) out.title = t('courseCreate.validation.required', { field: label });
    else if (title.length < TITLE_MIN) out.title = t('courseCreate.validation.minLength', { field: label, min: TITLE_MIN });
    else if (title.length > TITLE_MAX) out.title = t('courseCreate.validation.maxLength', { field: label, max: TITLE_MAX });
    return out;
  }, [createForm.title, t]);
  const titleError = touched.title ? errors.title : undefined;

  // Re-group the price as it is typed and put the caret back where it was. After every render, not
  // only when the text changed: a letter typed into the middle is dropped, the text stays the same,
  // and React still rewrites the field (which sends the caret to the end).
  const priceText = createForm.price === '' ? '' : formatNumber(createForm.price, { decimals: 0 });
  useLayoutEffect(() => {
    const input = priceRef.current;
    if (priceCaretRef.current == null || !input) return;
    const at = positionAfterDigits(priceText, priceCaretRef.current);
    priceCaretRef.current = null;
    if (document.activeElement === input) input.setSelectionRange(at, at);
  });

  const handlePriceChange = (e) => {
    const { value, selectionStart } = e.target;
    const caret = selectionStart ?? value.length;
    const digits = value.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, PRICE_MAX_DIGITS);
    priceCaretRef.current = Math.min(value.slice(0, caret).replace(/\D/g, '').length, digits.length);
    setCreateForm((f) => ({ ...f, price: digits }));
  };

  // What the banner says about a failed create: the course may exist (never re-sent by itself),
  // the API refused the data, or just a calm "try again". The server's own text is not shown.
  const reportFailure = useCallback((err) => {
    setFormRetryError(err);
    if (mayHaveBeenCreated(err)) setFormErrorMessage(t('courseCreate.errors.createMayHaveSucceeded'));
    else if (err?.code === 400 || err?.code === 422) setFormErrorMessage(t('courseCreate.errors.createRejected'));
    else setFormErrorMessage(null);
    setCreating(false);
  }, [t]);

  // Send the create (after a short pause that swallows a double click). `fresh` starts the retry
  // count over; a manual retry after a failure carries on from the banner's own count.
  const sendCreate = useCallback(({ fresh }) => {
    setCreating(true);
    setFormRetryError(null);
    setFormErrorMessage(null);
    if (fresh) setFormRetryCount(0);

    clearTimeout(submitDebounceRef.current);
    submitDebounceRef.current = setTimeout(async () => {
      try {
        // Creating is not idempotent: submitFormWithRetry re-sends it only when the request cannot
        // have been processed (offline, or 429), never after a timeout or a 5xx.
        const created = await submitFormWithRetry(
          async (_retryAttempt) => createCourse({
            title: createForm.title.trim(),
            description: createForm.description.trim() || undefined,
            level: createForm.level,
            price: Number(createForm.price) || 0,
            currency: 'UZS',
            thumbnail: createForm.thumbnail || undefined,
          }),
          {
            maxRetries: 3,
            timeout: 30000,
            context: 'Course creation',
            idempotent: false,
            t,
            onRetry: (retryInfo) => {
              setFormRetryError(retryInfo);
              setFormRetryCount(retryInfo.retryCount);
              if (retryInfo.isNetworkError) {
                setAutoRetryDelay(retryInfo.nextDelay);
              }
            },
          },
        );

        navigate(`/courses/edit/${created.id}`, { replace: true });
      } catch (err) {
        reportFailure(err);
      }
    }, 300);
  }, [createForm, navigate, reportFailure, t]);

  // The button is never greyed out for an incomplete form: pressing it says what is missing, in
  // place, and puts the cursor in that field.
  const attemptCreate = useCallback((fresh) => {
    if (creating) return;
    if (errors.title) {
      setTouched((prev) => ({ ...prev, title: true }));
      if (titleRef.current) titleRef.current.focus();
      return;
    }
    sendCreate({ fresh });
  }, [creating, errors, sendCreate]);

  const handleCreateCourse = (e) => {
    e.preventDefault();
    attemptCreate(true);
  };

  // Retry failed form submission
  const handleRetryFormSubmission = () => attemptCreate(false);

  // The card as it will look in the catalogue (the same component, without a link or menu).
  const previewCourse = {
    id: 'preview',
    title: createForm.title.trim() || t('courseCreate.preview.untitled'),
    image: createForm.thumbnail || null,
    description: createForm.description.trim() || null,
    level: createForm.level,
    category: null,
    price: Number(createForm.price) || 0,
    currency: 'UZS',
    isPublished: false,
    lessonsTotal: 0,
    enrollmentsTotal: 0,
    testsTotal: 0,
    createdAt: null,
    isMine: true,
    ownerName: null,
  };

  return (
    <FormErrorBoundary>
      <div className="course-creation-page ccx-page" data-page="form">
        <header className="page-head page-head--back">
          <Link
            to="/courses"
            className="ds-btn ds-btn-secondary ds-btn-icon page-back"
            aria-label={t('courseCreate.createHeader.backAria')}
          >
            <ArrowLeft aria-hidden="true" />
          </Link>
          <div className="page-head-text">
            <p className="page-eyebrow">{t('courseCreate.createHeader.back')}</p>
            <h1 className="page-title">{t('courseCreate.createHeader.title')}</h1>
            <p className="page-sub">{t('courseCreate.createHeader.subtitle')}</p>
          </div>
        </header>

        <form onSubmit={handleCreateCourse} className="ccx-form" aria-busy={creating} noValidate>
          {formRetryError && (
            <FormErrorDisplay
              error={formRetryError}
              message={formErrorMessage || undefined}
              onRetry={handleRetryFormSubmission}
              retryCount={formRetryCount}
              maxRetries={3}
              isRetrying={creating}
              autoRetryDelay={autoRetryDelay}
            />
          )}

          <div className="ccx-grid">
            {/* Details */}
            <section className="section-card ccx-details" aria-labelledby="ccx-details-title">
              <CardHead
                id="ccx-details-title"
                icon={<BookOpen />}
                tone="brand"
                title={t('courseCreate.details.cardTitle')}
                desc={t('courseCreate.details.cardDesc')}
              />

              <div className="ccx-fields">
                <div className="ccx-field">
                  <label className="ccx-label" htmlFor="ccx-title">
                    {t('courseCreate.details.titleLabel')}<span className="ccx-req" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="ccx-title"
                    ref={titleRef}
                    type="text"
                    value={createForm.title}
                    onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))}
                    onBlur={() => setTouched((prev) => ({ ...prev, title: true }))}
                    placeholder={t('courseCreate.details.titlePlaceholder')}
                    className="ccx-input"
                    autoComplete="off"
                    aria-required="true"
                    aria-invalid={Boolean(titleError)}
                    aria-describedby={titleError ? 'ccx-title-error' : 'ccx-title-hint'}
                  />
                  <FieldNote id="ccx-title" hint={t('courseCreate.details.titleHelp')} error={titleError} />
                </div>

                <div className="ccx-field">
                  <label className="ccx-label" htmlFor="ccx-description">{t('courseCreate.details.descriptionLabel')}</label>
                  <textarea
                    id="ccx-description"
                    value={createForm.description}
                    onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder={t('courseCreate.details.descriptionPlaceholder')}
                    rows={4}
                    className="ccx-input ccx-textarea"
                    aria-describedby="ccx-description-hint"
                  />
                  <FieldNote id="ccx-description" hint={t('courseCreate.details.descriptionHelp')} />
                </div>

                <div className="ccx-row">
                  <div className="ccx-field">
                    <label className="ccx-label" htmlFor="ccx-level">{t('courseCreate.details.levelLabel')}</label>
                    <Dropdown
                      id="ccx-level"
                      value={createForm.level}
                      onChange={(val) => setCreateForm((f) => ({ ...f, level: val }))}
                      ariaLabel={t('courseCreate.details.levelLabel')}
                      className="ccx-select"
                      options={[
                        { value: 'BEGINNER', label: t('courseCreate.levels.beginner') },
                        { value: 'INTERMEDIATE', label: t('courseCreate.levels.intermediate') },
                        { value: 'ADVANCED', label: t('courseCreate.levels.advanced') },
                      ]}
                    />
                  </div>
                  <div className="ccx-field ccx-field--price">
                    <label className="ccx-label" htmlFor="ccx-price">{t('courseCreate.details.priceLabel')}</label>
                    <div className="ccx-money">
                      <input
                        id="ccx-price"
                        ref={priceRef}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={priceText}
                        onChange={handlePriceChange}
                        placeholder={t('courseCreate.details.pricePlaceholder')}
                        className="ccx-input"
                        aria-describedby="ccx-price-hint"
                      />
                      <span className="ccx-money-unit" aria-hidden="true">{currencyLabel('UZS', { lang })}</span>
                    </div>
                  </div>
                  <FieldNote id="ccx-price" hint={t('courseCreate.details.priceHelp')} className="ccx-note--row" />
                </div>
              </div>
            </section>

            {/* Cover constructor */}
            <section className="section-card ccx-cover" aria-labelledby="ccx-cover-title">
              <CardHead
                id="ccx-cover-title"
                icon={<ImageIcon />}
                tone="coral"
                title={t('courseCreate.cover.cardTitle')}
                desc={t('courseCreate.cover.cardDesc')}
              />
              <CourseCoverPicker
                value={createForm.thumbnail}
                onChange={(url) => setCreateForm((f) => ({ ...f, thumbnail: url }))}
                initialTitle={createForm.title}
                courseTitle={createForm.title}
              />
            </section>

            {/* Live preview of the catalogue card */}
            <section className="section-card ccx-preview" aria-labelledby="ccx-preview-title">
              <CardHead
                id="ccx-preview-title"
                icon={<Eye />}
                tone="info"
                title={t('courseCreate.preview.cardTitle')}
                desc={t('courseCreate.preview.hint')}
              />
              <div className="ccx-preview-card">
                <CourseListCard course={previewCourse} preview />
              </div>
            </section>
          </div>

          {/* Actions stay in reach while the form scrolls */}
          <div className="ui-sticky-actions ccx-actions">
            <Link to="/courses" className="ds-btn ds-btn-ghost">{t('courseCreate.actions.cancel')}</Link>
            <button
              type="submit"
              className={`ds-btn ds-btn-primary${creating ? ' is-loading' : ''}`}
              aria-busy={creating || undefined}
            >
              {t('courseCreate.actions.createCourse')}
            </button>
            <span className="visually-hidden" role="status">{creating ? t('courseCreate.actions.creating') : ''}</span>
          </div>
        </form>
      </div>
    </FormErrorBoundary>
  );
};

export default CourseCreation;
